import type { RagaRecord } from '@swarasense/shared';

/**
 * Ranked, alias-aware raga name search (Task 5's searchable ragam picker).
 * Pure and framework-free so it's cheap to unit-test in isolation from the
 * seed client (`./seed.ts`, which wires this over the loaded bundle) and the
 * combobox widget (`../ui/combobox.ts`) that renders its results.
 */

/** Match quality for one candidate string against a (lowercased) query —
 * higher is a better match; 0 means no match at all. */
function matchScore(query: string, candidate: string): number {
  if (candidate === query) return 3; // exact
  if (candidate.startsWith(query)) return 2; // prefix
  if (candidate.includes(query)) return 1; // substring
  return 0;
}

/** Best match score for a record: its name OR any of its aliases, whichever
 * scores highest (an alias hit is just as good as a name hit). */
function bestScore(query: string, record: RagaRecord): number {
  let best = matchScore(query, record.name.toLowerCase());
  for (const alias of record.aliases) {
    const score = matchScore(query, alias.toLowerCase());
    if (score > best) best = score;
  }
  return best;
}

/**
 * Ranks `records` against `query` by name-or-alias match, case-insensitively:
 * exact > prefix > substring, with no-match records dropped. An empty (or
 * whitespace-only) query returns every record, in its original order — the
 * "browse everything" state before the user has typed anything.
 *
 * `Array.prototype.sort` is stable (guaranteed since ES2019), so records
 * tied on score keep their relative input order rather than shuffling.
 */
export function rankRagaMatches(query: string, records: RagaRecord[]): RagaRecord[] {
  const q = query.trim().toLowerCase();
  if (q === '') return records;

  return records
    .map((record) => ({ record, score: bestScore(q, record) }))
    .filter((entry) => entry.score > 0)
    .sort((a, b) => b.score - a.score)
    .map((entry) => entry.record);
}
